import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Pencil, IndianRupee, Settings2, Zap,
  CheckCircle2, AlertTriangle, ChevronDown, ChevronUp, RefreshCw,
} from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import Modal from '../../components/Modal';
import SearchSelect from '../../components/SearchSelect';
import { useAuth } from '../../context/AuthContext';
import {
  maintenanceCharges as api,
  blocks as blocksApi,
  type MaintenanceCharge,
  type MaintenanceChargeConfig,
  type Block,
  type ApiError,
} from '../../services/api';

/* ── styles ── */
const inp: React.CSSProperties = {
  width: '100%', padding: '9px 12px', background: 'var(--nx-surface-3)',
  border: '1px solid var(--nx-border)', borderRadius: 8, fontSize: 13,
  color: 'var(--nx-text-primary)', outline: 'none', boxSizing: 'border-box',
};
const lbl: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)',
  marginBottom: 5, letterSpacing: '0.05em', textTransform: 'uppercase',
};

const APPLICABLE_OPTIONS: { value: MaintenanceCharge['applicableTo']; label: string }[] = [
  { value: 'all',    label: 'All Residents' },
  { value: 'owner',  label: 'Owners Only' },
  { value: 'tenant', label: 'Tenants Only' },
  { value: 'family', label: 'Family Members Only' },
];

const applicableColor = (a: string) =>
  a === 'owner'  ? { bg: 'rgba(26,108,246,0.1)',  text: '#1A6CF6' }
  : a === 'tenant' ? { bg: 'rgba(109,40,217,0.1)', text: '#6D28D9' }
  : a === 'family' ? { bg: 'rgba(16,185,129,0.1)', text: '#10B981' }
  :                   { bg: 'rgba(100,116,139,0.1)', text: '#64748B' };

const CHARGE_INIT = { name: '', amount: '', applicableTo: 'all' as MaintenanceCharge['applicableTo'], blockId: '' };

const CURRENT_MONTH = new Date().toISOString().slice(0, 7); // "YYYY-MM"

const MonthlyChargesPage: React.FC = () => {
  const { user } = useAuth();
  const sid = user?.societyId ?? null;

  /* data */
  const [charges, setCharges]   = useState<MaintenanceCharge[]>([]);
  const [config, setConfig]     = useState<Partial<MaintenanceChargeConfig>>({
    dueDayOfMonth: 5, gracePeriodDays: 5, lateFeePct: 2, isActive: true, gstRatePct: 0, lateFeeMode: 'FLAT_PCT',
    autoGenerateInvoices: false,
  });
  const [blocks, setBlocks]     = useState<Block[]>([]);
  const [loading, setLoading]   = useState(true);

  /* UI */
  const [showChargeForm, setShowChargeForm] = useState(false);
  const [editCharge, setEditCharge]         = useState<MaintenanceCharge | null>(null);
  const [chargeForm, setChargeForm]         = useState(CHARGE_INIT);
  const [saving, setSaving]                 = useState(false);
  const [formErr, setFormErr]               = useState('');
  const [configExpanded, setConfigExpanded] = useState(true);
  const [savingConfig, setSavingConfig]     = useState(false);
  const [configMsg, setConfigMsg]           = useState('');

  /* generate */
  const [genMonth, setGenMonth]   = useState(CURRENT_MONTH);
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState<{ generated: number } | null>(null);
  const [genErr, setGenErr]       = useState('');

  const load = useCallback(async () => {
    if (!sid) { setLoading(false); return; }
    setLoading(true);
    const [ch, blk] = await Promise.allSettled([
      api.list(sid),
      blocksApi.list(sid),
      api.getConfig(sid).then(c => setConfig(c)).catch(() => {}),
    ]);
    if (ch.status === 'fulfilled') setCharges(ch.value);
    if (blk.status === 'fulfilled') setBlocks(blk.value);
    setLoading(false);
  }, [sid]);

  useEffect(() => { load(); }, [load]);

  /* expected monthly total (per charge × applicability) */
  const expectedTotal = charges
    .filter(c => c.isActive)
    .reduce((sum, c) => sum + c.amount, 0);

  /* open add / edit */
  const openAdd = () => {
    setEditCharge(null);
    setChargeForm(CHARGE_INIT);
    setFormErr('');
    setShowChargeForm(true);
  };
  const openEdit = (c: MaintenanceCharge) => {
    setEditCharge(c);
    setChargeForm({ name: c.name, amount: String(c.amount), applicableTo: c.applicableTo, blockId: c.blockId ?? '' });
    setFormErr('');
    setShowChargeForm(true);
  };

  const saveCharge = async () => {
    if (!sid) return;
    if (!chargeForm.name.trim()) { setFormErr('Charge name is required.'); return; }
    const amt = parseFloat(chargeForm.amount);
    if (!amt || amt <= 0) { setFormErr('Enter a valid amount.'); return; }
    setSaving(true); setFormErr('');
    try {
      const payload: Partial<MaintenanceCharge> = {
        name: chargeForm.name.trim(),
        amount: amt,
        applicableTo: chargeForm.applicableTo,
        blockId: chargeForm.blockId || null,
        isActive: true,
      };
      if (editCharge) {
        const updated = await api.update(sid, editCharge.id, payload);
        setCharges(prev => prev.map(c => c.id === editCharge.id ? updated : c));
      } else {
        const created = await api.create(sid, payload);
        setCharges(prev => [...prev, created]);
      }
      setShowChargeForm(false);
    } catch (e) {
      setFormErr((e as ApiError).message ?? 'Failed to save charge.');
    } finally {
      setSaving(false);
    }
  };

  const deleteCharge = async (id: string) => {
    if (!sid || !window.confirm('Delete this charge?')) return;
    await api.remove(sid, id).catch(() => {});
    setCharges(prev => prev.filter(c => c.id !== id));
  };

  const toggleActive = async (charge: MaintenanceCharge) => {
    if (!sid) return;
    const updated = await api.update(sid, charge.id, { isActive: !charge.isActive }).catch(() => null);
    if (updated) setCharges(prev => prev.map(c => c.id === charge.id ? updated : c));
  };

  const saveConfig = async () => {
    if (!sid) return;
    setSavingConfig(true); setConfigMsg('');
    try {
      await api.saveConfig(sid, config);
      setConfigMsg('Settings saved.');
    } catch {
      setConfigMsg('Failed to save settings.');
    } finally {
      setSavingConfig(false);
      setTimeout(() => setConfigMsg(''), 3000);
    }
  };

  const generateInvoices = async () => {
    if (!sid) return;
    setGenerating(true); setGenErr(''); setGenResult(null);
    try {
      const result = await api.generateInvoices(sid, genMonth);
      setGenResult(result);
    } catch (e) {
      setGenErr((e as ApiError).message ?? 'Failed to generate invoices.');
    } finally {
      setGenerating(false);
    }
  };

  const [applyingLateFees, setApplyingLateFees] = useState(false);
  const [lateFeeResult, setLateFeeResult] = useState<{ updated: number } | null>(null);
  const [lateFeeErr, setLateFeeErr] = useState('');

  const applyLateFees = async () => {
    if (!sid) return;
    setApplyingLateFees(true); setLateFeeErr(''); setLateFeeResult(null);
    try {
      const result = await api.applyLateFees(sid);
      setLateFeeResult(result);
    } catch (e) {
      setLateFeeErr((e as ApiError).message ?? 'Failed to apply late fees.');
    } finally {
      setApplyingLateFees(false);
    }
  };

  return (
    <PageLayout
      title="Monthly Charges"
      subtitle="Configure recurring maintenance dues and generate invoices for residents"
      icon={<IndianRupee size={18} />}
      actions={
        <button className="nx-btn nx-btn-primary nx-btn-sm" onClick={openAdd} style={{ gap: 5 }}>
          <Plus size={13} /> Add Charge
        </button>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, alignItems: 'start' }}>
        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Billing Settings card */}
          <div className="nx-card" style={{ padding: 20 }}>
            <button
              onClick={() => setConfigExpanded(e => !e)}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Settings2 size={15} style={{ color: 'var(--nx-blue)' }} />
                <span style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--nx-text-primary)' }}>
                  Billing Settings
                </span>
              </div>
              {configExpanded ? <ChevronUp size={15} color="var(--nx-text-muted)" /> : <ChevronDown size={15} color="var(--nx-text-muted)" />}
            </button>

            {configExpanded && (
              <div style={{ marginTop: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14, marginBottom: 16 }}>
                  <div>
                    <label style={lbl}>Due Day of Month</label>
                    <input
                      type="number" min={1} max={28} style={inp}
                      value={config.dueDayOfMonth ?? 5}
                      onChange={e => setConfig(c => ({ ...c, dueDayOfMonth: Number(e.target.value) }))}
                    />
                    <p style={{ fontSize: 10, color: 'var(--nx-text-muted)', marginTop: 4 }}>Invoices generated on this day each month</p>
                  </div>
                  <div>
                    <label style={lbl}>Grace Period (days)</label>
                    <input
                      type="number" min={0} max={30} style={inp}
                      value={config.gracePeriodDays ?? 5}
                      onChange={e => setConfig(c => ({ ...c, gracePeriodDays: Number(e.target.value) }))}
                    />
                    <p style={{ fontSize: 10, color: 'var(--nx-text-muted)', marginTop: 4 }}>Days after due before late fee applies</p>
                  </div>
                  <div>
                    <label style={lbl}>Late Fee (%/month)</label>
                    <input
                      type="number" min={0} max={10} step={0.5} style={inp}
                      value={config.lateFeePct ?? 2}
                      onChange={e => setConfig(c => ({ ...c, lateFeePct: Number(e.target.value) }))}
                    />
                    <p style={{ fontSize: 10, color: 'var(--nx-text-muted)', marginTop: 4 }}>% of outstanding added as late fee</p>
                  </div>
                  <div>
                    <label style={lbl}>GST Rate (%)</label>
                    <input
                      type="number" min={0} max={28} step={0.5} style={inp}
                      value={config.gstRatePct ?? 0}
                      onChange={e => setConfig(c => ({ ...c, gstRatePct: Number(e.target.value) }))}
                    />
                    <p style={{ fontSize: 10, color: 'var(--nx-text-muted)', marginTop: 4 }}>Split evenly as CGST + SGST on invoices; 0 = no GST</p>
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={lbl}>Late Fee Mode</label>
                  <select
                    style={{ ...inp, maxWidth: 260 }}
                    value={config.lateFeeMode ?? 'FLAT_PCT'}
                    onChange={e => setConfig(c => ({ ...c, lateFeeMode: e.target.value as 'FLAT_PCT' | 'COMPOUND_PCT' }))}
                  >
                    <option value="FLAT_PCT">Flat % (applied once)</option>
                    <option value="COMPOUND_PCT">Compound % (applied each sweep)</option>
                  </select>
                </div>
                <div style={{ marginBottom: 16, padding: '12px 14px', background: 'var(--nx-surface-2)', borderRadius: 8, border: '1px solid var(--nx-border)' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={config.autoGenerateInvoices ?? false}
                      onChange={e => setConfig(c => ({ ...c, autoGenerateInvoices: e.target.checked }))}
                      style={{ marginTop: 2 }}
                    />
                    <span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text-primary)', display: 'block' }}>
                        Auto-generate invoices every month
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--nx-text-muted)', lineHeight: 1.5 }}>
                        When on, this month's invoices are created automatically for every occupied unit — the secretary
                        doesn't need to click "Generate Invoices" each month. When off, generation stays manual (below).
                      </span>
                    </span>
                  </label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button
                    className="nx-btn nx-btn-primary nx-btn-sm"
                    onClick={saveConfig}
                    disabled={savingConfig}
                    style={{ gap: 5 }}
                  >
                    {savingConfig ? <RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> : null}
                    Save Settings
                  </button>
                  {configMsg && (
                    <span style={{ fontSize: 12, color: configMsg.includes('Failed') ? 'var(--nx-red)' : 'var(--nx-green)' }}>
                      {configMsg}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Charges table */}
          <div className="nx-card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 2 }}>
                  Charge Line Items
                </h3>
                <p style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>
                  {charges.filter(c => c.isActive).length} active charges · ₹{expectedTotal.toLocaleString('en-IN')}/unit expected
                </p>
              </div>
              <button className="nx-btn nx-btn-outline nx-btn-sm" onClick={openAdd} style={{ gap: 5 }}>
                <Plus size={12} /> Add
              </button>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--nx-text-muted)', fontSize: 13 }}>Loading…</div>
            ) : charges.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '36px 0' }}>
                <IndianRupee size={28} style={{ color: 'var(--nx-text-muted)', marginBottom: 10 }} />
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 4 }}>No charges configured yet</div>
                <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 14 }}>Add maintenance fee, water charges, parking, etc.</div>
                <button className="nx-btn nx-btn-primary nx-btn-sm" onClick={openAdd} style={{ gap: 5 }}>
                  <Plus size={12} /> Add First Charge
                </button>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {['Charge Name', 'Amount', 'Applicable To', 'Block', 'Status', ''].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '6px 10px', fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', color: 'var(--nx-text-muted)', borderBottom: '1px solid var(--nx-surface-3)', textTransform: 'uppercase' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {charges.map(c => {
                    const ac = applicableColor(c.applicableTo);
                    return (
                      <tr key={c.id} style={{ opacity: c.isActive ? 1 : 0.5 }}>
                        <td style={{ padding: '10px 10px', color: 'var(--nx-text-primary)', fontWeight: 500 }}>{c.name}</td>
                        <td style={{ padding: '10px 10px', color: 'var(--nx-text-primary)', fontWeight: 600 }}>
                          ₹{c.amount.toLocaleString('en-IN')}
                        </td>
                        <td style={{ padding: '10px 10px' }}>
                          <span style={{ padding: '2px 9px', borderRadius: 99, fontSize: 11, fontWeight: 500, background: ac.bg, color: ac.text }}>
                            {APPLICABLE_OPTIONS.find(o => o.value === c.applicableTo)?.label ?? c.applicableTo}
                          </span>
                        </td>
                        <td style={{ padding: '10px 10px', color: 'var(--nx-text-muted)', fontSize: 12 }}>
                          {c.blockName ?? (c.blockId ? c.blockId : 'All Blocks')}
                        </td>
                        <td style={{ padding: '10px 10px' }}>
                          <button
                            onClick={() => toggleActive(c)}
                            style={{ padding: '2px 9px', borderRadius: 99, fontSize: 11, fontWeight: 500, border: 'none', cursor: 'pointer', background: c.isActive ? 'rgba(16,185,129,0.1)' : 'rgba(148,163,184,0.1)', color: c.isActive ? '#10B981' : '#64748B' }}
                          >
                            {c.isActive ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td style={{ padding: '10px 10px' }}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button
                              onClick={() => openEdit(c)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 6, color: 'var(--nx-text-muted)', display: 'inline-flex' }}
                              title="Edit"
                            >
                              <Pencil size={13} />
                            </button>
                            <button
                              onClick={() => deleteCharge(c.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 6, color: 'var(--nx-text-muted)', display: 'inline-flex' }}
                              title="Delete"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Right column — summary + generate */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Summary card */}
          <div className="nx-card" style={{ padding: 20 }}>
            <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 13, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 14 }}>
              Charge Summary
            </h3>
            {charges.filter(c => c.isActive).length === 0 ? (
              <p style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>No active charges yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {charges.filter(c => c.isActive).map(c => {
                  const ac = applicableColor(c.applicableTo);
                  return (
                    <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500, color: 'var(--nx-text-primary)' }}>{c.name}</div>
                        <div style={{ fontSize: 10, color: ac.text }}>
                          {APPLICABLE_OPTIONS.find(o => o.value === c.applicableTo)?.label}
                          {c.blockName ? ` · ${c.blockName}` : ''}
                        </div>
                      </div>
                      <div style={{ fontWeight: 700, color: 'var(--nx-text-primary)' }}>
                        ₹{c.amount.toLocaleString('en-IN')}
                      </div>
                    </div>
                  );
                })}
                <div style={{ borderTop: '1px solid var(--nx-border)', paddingTop: 10, marginTop: 4, display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ fontWeight: 600, color: 'var(--nx-text-secondary)' }}>Total per unit</span>
                  <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--nx-blue)' }}>₹{expectedTotal.toLocaleString('en-IN')}</span>
                </div>
              </div>
            )}
          </div>

          {/* Generate invoices card */}
          <div className="nx-card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <Zap size={15} style={{ color: '#F59E0B' }} />
              <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 13, fontWeight: 700, color: 'var(--nx-text-primary)' }}>
                Generate Invoices
              </h3>
            </div>
            <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 12, lineHeight: 1.55 }}>
              Creates one invoice per occupied unit for the selected month based on active charges.
            </p>
            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Billing Month</label>
              <input
                type="month" style={inp} value={genMonth}
                onChange={e => setGenMonth(e.target.value)}
              />
            </div>

            {genResult && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', borderRadius: 8, marginBottom: 12,
                background: genResult.generated > 0 ? 'rgba(16,185,129,0.08)' : 'rgba(245,158,11,0.08)',
                border: `1px solid ${genResult.generated > 0 ? 'rgba(16,185,129,0.2)' : 'rgba(245,158,11,0.2)'}`,
              }}>
                <CheckCircle2 size={14} style={{ color: genResult.generated > 0 ? '#10B981' : '#D97706', flexShrink: 0, marginTop: 1 }} />
                <span style={{ fontSize: 12, color: genResult.generated > 0 ? '#10B981' : '#D97706', fontWeight: 500 }}>
                  {genResult.generated > 0
                    ? `${genResult.generated} invoice${genResult.generated !== 1 ? 's' : ''} generated successfully.`
                    : `Invoices for ${new Date(genMonth + '-01').toLocaleString('en-IN', { month: 'long', year: 'numeric' })} already exist — no new invoices were created.`
                  }
                </span>
              </div>
            )}
            {genErr && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, marginBottom: 12 }}>
                <AlertTriangle size={14} style={{ color: '#DC2626', flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: '#DC2626' }}>{genErr}</span>
              </div>
            )}

            <button
              className="nx-btn nx-btn-primary"
              onClick={generateInvoices}
              disabled={generating || charges.filter(c => c.isActive).length === 0}
              style={{ width: '100%', justifyContent: 'center', gap: 6 }}
            >
              {generating
                ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> Generating…</>
                : <><Zap size={13} /> Generate for {new Date(genMonth + '-01').toLocaleString('en-IN', { month: 'long', year: 'numeric' })}</>
              }
            </button>

            <p style={{ fontSize: 10, color: 'var(--nx-text-muted)', marginTop: 8, lineHeight: 1.5 }}>
              Already-generated invoices for the same month won't be duplicated.
            </p>

            {lateFeeResult && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 8, marginTop: 12 }}>
                <CheckCircle2 size={14} style={{ color: '#10B981', flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: '#10B981', fontWeight: 500 }}>{lateFeeResult.updated} overdue invoice{lateFeeResult.updated !== 1 ? 's' : ''} updated with late fees.</span>
              </div>
            )}
            {lateFeeErr && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, marginTop: 12 }}>
                <AlertTriangle size={14} style={{ color: '#DC2626', flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: '#DC2626' }}>{lateFeeErr}</span>
              </div>
            )}
            <button
              className="nx-btn nx-btn-outline"
              onClick={applyLateFees}
              disabled={applyingLateFees}
              style={{ width: '100%', justifyContent: 'center', gap: 6, marginTop: 12 }}
            >
              {applyingLateFees
                ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> Applying…</>
                : <><Zap size={13} /> Apply Late Fees Now</>
              }
            </button>
            <p style={{ fontSize: 10, color: 'var(--nx-text-muted)', marginTop: 8, lineHeight: 1.5 }}>
              Runs automatically every night; use this to apply late fees immediately for testing or a manual sweep.
            </p>
          </div>

          {/* Billing settings summary */}
          <div className="nx-card" style={{ padding: 16 }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Current Settings</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                { label: 'Due day', value: `${config.dueDayOfMonth ?? 5}th of month` },
                { label: 'Grace period', value: `${config.gracePeriodDays ?? 5} days` },
                { label: 'Late fee', value: `${config.lateFeePct ?? 2}% / month` },
                { label: 'GST rate', value: `${config.gstRatePct ?? 0}%` },
                { label: 'Late fee mode', value: config.lateFeeMode === 'COMPOUND_PCT' ? 'Compound' : 'Flat' },
                { label: 'Invoice generation', value: config.autoGenerateInvoices ? 'Automatic' : 'Manual' },
              ].map(r => (
                <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                  <span style={{ color: 'var(--nx-text-muted)' }}>{r.label}</span>
                  <span style={{ fontWeight: 600, color: 'var(--nx-text-primary)' }}>{r.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Add / Edit Charge Modal */}
      {showChargeForm && <Modal
        onClose={() => setShowChargeForm(false)}
        onSubmit={e => { e.preventDefault(); saveCharge(); }}
        title={editCharge ? 'Edit Charge' : 'Add Charge'}
        submitLabel={editCharge ? 'Save Changes' : 'Add Charge'}
        isLoading={saving}
        error={formErr}
      >
        <div>
          <label style={lbl}>Charge Name *</label>
          <input
            style={inp} placeholder="e.g. Maintenance Fee, Water Charges, Parking"
            value={chargeForm.name}
            onChange={e => setChargeForm(f => ({ ...f, name: e.target.value }))}
          />
        </div>
        <div>
          <label style={lbl}>Amount (₹/month) *</label>
          <input
            type="number" min={0} style={inp} placeholder="0"
            value={chargeForm.amount}
            onChange={e => setChargeForm(f => ({ ...f, amount: e.target.value }))}
          />
        </div>
        <div>
          <label style={lbl}>Applicable To *</label>
          <select
            style={inp}
            value={chargeForm.applicableTo}
            onChange={e => setChargeForm(f => ({ ...f, applicableTo: e.target.value as MaintenanceCharge['applicableTo'] }))}
          >
            {APPLICABLE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={lbl}>Block (optional)</label>
          <SearchSelect
            value={chargeForm.blockId}
            onChange={v => setChargeForm(f => ({ ...f, blockId: v }))}
            options={blocks.map(b => ({ value: b.id, label: b.name }))}
            placeholder="All Blocks"
          />
          <p style={{ fontSize: 10, color: 'var(--nx-text-muted)', marginTop: 4 }}>Leave as "All Blocks" to apply society-wide</p>
        </div>
      </Modal>}
    </PageLayout>
  );
};

export default MonthlyChargesPage;
